import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiSearch,
  FiBell,
  FiChevronDown,
  FiChevronUp,
  FiFilter,
  FiCalendar,
} from "react-icons/fi";
import { noticeAPI } from "../services/api";
import { formatDate } from "../utils/helpers";
import { useDataRefresh } from "../hooks/useDataRefresh";
import LoadingSpinner from "../components/common/LoadingSpinner";

const CATEGORIES = [
  "All",
  "General",
  "Academic",
  "Sports",
  "Events",
  "Urgent",
  "Holiday",
  "Exam",
];

const categoryColors = {
  Academic: "bg-blue-100 text-blue-700 border-blue-200",
  Holiday: "bg-green-100 text-green-700 border-green-200",
  Exam: "bg-purple-100 text-purple-700 border-purple-200",
  Event: "bg-orange-100 text-orange-700 border-orange-200",
  General: "bg-gray-100 text-gray-700 border-gray-200",
  Urgent: "bg-red-100 text-red-700 border-red-200",
};

const Notices = () => {
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("All");
  const [expanded, setExpanded] = useState(null);

  const { data: notices = [], loading } = useDataRefresh(
    async () => {
      const response = await noticeAPI.getAll();
      return response?.data?.data?.notices || [];
    },
    "notices",
    { pollInterval: 10000 }, // Refresh every 10 seconds
  );

  useEffect(() => {
    let result = notices;
    if (category !== "All")
      result = result.filter((n) => n.category === category);
    if (search.trim())
      result = result.filter((n) =>
        n.title.toLowerCase().includes(search.toLowerCase()),
      );
    setFiltered(result);
    setExpanded(null);
  }, [category, search, notices]);

  return (
    <div>
      <div className="hero-gradient py-24 text-center relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              "radial-gradient(circle, #bfdbfe 1px, transparent 1px)",
            backgroundSize: "40px 40px",
          }}
        />
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative"
        >
          <p className="text-secondary-400 text-sm font-semibold uppercase tracking-widest mb-3">
            Stay Informed
          </p>
          <h1 className="text-5xl font-serif font-bold text-white mb-4">
            Notices
          </h1>
          <p className="text-white/70 text-lg max-w-xl mx-auto">
            Important announcements and notices from Baby Star
            Montessori Pvt. Ltd.
          </p>
        </motion.div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Search & Filter */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search notices..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-400"
            />
          </div>
          <div className="flex items-center gap-2">
            <FiFilter className="text-gray-400 flex-shrink-0" />
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-200 bg-white"
            >
              {CATEGORIES.map((c) => (
                <option key={c}>{c}</option>
              ))}
            </select>
          </div>
        </div>

        {loading ? (
          <LoadingSpinner />
        ) : (
          <div className="space-y-4">
            {filtered.map((notice, idx) => (
              <motion.div
                key={notice._id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: idx * 0.06 }}
                className="bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-sm"
              >
                <button
                  onClick={() =>
                    setExpanded(expanded === notice._id ? null : notice._id)
                  }
                  className="w-full flex items-center gap-4 p-5 text-left hover:bg-gray-50 transition-colors"
                >
                  <div
                    className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                      notice.category === "Urgent"
                        ? "bg-red-100"
                        : "bg-primary-50"
                    }`}
                  >
                    <FiBell
                      className={`w-5 h-5 ${notice.category === "Urgent" ? "text-red-500" : "text-primary-500"}`}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap mb-1">
                      <h3 className="font-bold text-primary-500 text-sm">
                        {notice.title}
                      </h3>
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium border ${categoryColors[notice.category] || categoryColors.General}`}
                      >
                        {notice.category}
                      </span>
                    </div>
                    {notice.publishDate && (
                      <div className="flex items-center gap-1.5 text-gray-400 text-xs">
                        <FiCalendar className="w-3 h-3" />
                        {formatDate(notice.publishDate)}
                      </div>
                    )}
                  </div>
                  {expanded === notice._id ? (
                    <FiChevronUp className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  ) : (
                    <FiChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  )}
                </button>

                <AnimatePresence>
                  {expanded === notice._id && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="px-5 pb-5 pt-0 border-t border-gray-100">
                        <p className="text-gray-600 text-sm leading-relaxed mt-4">
                          {notice.content}
                        </p>
                        {notice.expiryDate && (
                          <p className="mt-3 text-xs text-gray-400">
                            Notice expires: {formatDate(notice.expiryDate)}
                          </p>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}

            {filtered.length === 0 && (
              <div className="text-center py-16">
                <FiBell className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 font-medium">No notices found</p>
                <p className="text-gray-400 text-sm mt-1">
                  Try adjusting your search or filter
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Notices;
